// app/providers.tsx
'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';

import { POSTHOG_OPTED_IN_EVENT } from '@/lib/analytics/activation';
import {
  POSTHOG_STORAGE_KEY_PATTERNS,
  createPostHogConsentGate,
} from '@/lib/analytics/posthogConsentGate';
import { removeBrowserStorageKeysMatching } from '@/lib/analytics/removeStorageKeys';
import {
  isBenignException,
  redactUrlProperties,
} from '@/utils/posthogExceptionFilter';

// Extend Window interface to include our custom function
declare global {
  interface Window {
    updatePostHogConsent?: (hasConsent: boolean) => void;
  }
}

// Defer non-critical work until the browser is idle. Falls back to a 1s
// setTimeout in environments without requestIdleCallback (Safari < 17). The
// 2s `timeout:` cap on rIC guarantees the callback fires even on a busy main
// thread — so analytics still attach within a bounded window.
function whenIdle(cb: () => void): void {
  if (typeof window === 'undefined') return;
  const rIC = (
    window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number;
    }
  ).requestIdleCallback;
  if (typeof rIC === 'function') {
    rIC(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 1000);
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env['NEXT_PUBLIC_POSTHOG_KEY'];
    const host = process.env['NEXT_PUBLIC_POSTHOG_HOST'];

    // Skip PostHog init if host is not configured (avoids loading config.js
    // from the page origin, which returns HTML and causes SyntaxError in dev)
    if (!key || !host) return;

    // Apply the GDPR consent gate (#4940, #11843). PostHog is NOT initialised
    // until analytics consent is granted — see posthogConsentGate.ts for why an
    // opted-out init is not enough (it still fetched config.js, /flags and four
    // extension scripts before any consent). opt_in/opt_out stays PostHog's
    // top-level switch once it has been initialised, so this single gate still
    // covers every capture site (activation funnel, match_* events) uniformly,
    // mirroring how Faro/GA/Facebook are gated. Never opt in under development
    // so a local cookie-banner accept can't pollute prod analytics.
    const initPostHog = () => {
      posthog.init(key, {
        debug: false,
        api_host: host,
        person_profiles: 'identified_only',
        persistence: 'memory', // Start with memory-only persistence
        // Belt and braces (#4940): init only ever runs after a grant, and the
        // gate's optIn() opts in immediately after — so this default only
        // matters if a future change reintroduces an init before consent.
        opt_out_capturing_by_default: true,
        defaults: '2025-05-24',
        autocapture: true,
        // ── Spis DPIA §12 #7: autocapture must never capture client-
        // confidential content ──────────────────────────────────────────
        // Spis document content, uploaded filenames, and chat/query text all
        // render into the DOM (element text, plus `value`/`title`/`alt`/
        // `placeholder`/`href` attributes) and would otherwise be swept up by
        // autocapture's $autocapture/$pageview element snapshots. These three
        // switches make masking the robust GLOBAL default — no reliance on
        // per-element opt-out — protecting advokátske tajomstvo / client PII
        // (GDPR Art. 5(1)(c) data-minimisation). Aggregate click analytics
        // still work: element tag, classes and css selector are unaffected.
        //   • mask_all_text             → drop captured element text content
        //   • mask_all_element_attributes → drop every captured element attr
        //     (value/title/alt/placeholder/href — where a filename or query
        //     string can hide)
        //   • mask_personal_data_properties → apply PostHog's PII heuristics to
        //     URL/property fields (defence in depth atop redactUrlProperties)
        mask_all_text: true,
        mask_all_element_attributes: true,
        mask_personal_data_properties: true,
        // Session replay is not enabled for this project, but if it is ever
        // switched on at the project level, force-mask all inputs and all text
        // so a recording can never replay document content or a typed query.
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
        },
        before_send: (event) => {
          if (!event) return event;
          if (isBenignException(event)) return null;
          // Strip magic-link tokens (`?token=` / `#token=`, and other
          // sensitive params) from every string of the event BEFORE it ships.
          // Autocapture fires `$pageview` on init, which races the verify
          // page's history-replace useEffect — without this redaction the
          // token reaches PostHog (#3841). Ships the redacted COPY; `null`
          // (cannot be redacted safely) drops the event.
          return redactUrlProperties(event);
        },
      });
    };

    const gate = createPostHogConsentGate(
      {
        init: initPostHog,
        optIn: () => {
          // captureEventName: false suppresses the default $opt_in noise
          // event — we don't need a separate consent-action event.
          posthog.opt_in_capturing({ captureEventName: false });
          posthog.set_config({ persistence: 'localStorage+cookie' });
          // Capturing is now live. Notify the session-aware consumer
          // (SupabaseAuthProvider) so a signup_completed suppressed
          // pre-consent fires immediately, instead of waiting for the next
          // auth event (#4966).
          window.dispatchEvent(new Event(POSTHOG_OPTED_IN_EVENT));
        },
        optOut: () => {
          posthog.opt_out_capturing();
          posthog.set_config({ persistence: 'memory' });
          // Clear any existing PostHog data
          posthog.reset();
        },
      },
      {
        allowOptIn: process.env.NODE_ENV !== 'development',
        // Defer the first init until the browser is idle: `posthog.init`
        // registers autocapture listeners and scans the DOM, which competes
        // with hydration. The `posthog` singleton no-ops capture calls until
        // init, so a usePostHog() consumer that fires early simply drops the
        // event — which is also exactly what consent requires.
        schedule: whenIdle,
        // A refusal on a page where PostHog never initialised still has to
        // remove what an earlier load stored (e.g. the pre-#11843 build,
        // which initialised at mount under the old consent revision).
        clearLeftovers: () =>
          removeBrowserStorageKeysMatching(POSTHOG_STORAGE_KEY_PATTERNS),
      }
    );

    // Registered synchronously so CookieConsent.tsx's onConsent/onChange
    // callbacks always find it on the window.
    window.updatePostHogConsent = (hasConsent: boolean) => {
      gate.update(hasConsent);
    };
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
